import React, { Component } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { observer } from "mobx-react";
import { observable } from "mobx";
import { Form, Modal, Button, Checkbox, Select, Message } from "semantic-ui-react";
import { CardElement } from "@stripe/react-stripe-js";
import cx from "classnames";
import StripeConnect from "~/models/StripeConnect";
import CustomerPayment from "~/models/CustomerPayment";
import ErrorWrapper from "~/components/wrapper/ErrorWrapper";
import FormStore from "~/stores/FormStore";
import { getValidationErrors } from "~/utils/ReactReformed/ValidateSchema";
import PromiseButton from "~/components/element/PromiseButton";

const cardOptions = {
  iconStyle: "solid",
  style: {
    base: {
      iconColor: "#000",
      color: "#000",
      fontWeight: 500,
      fontFamily: "Roboto, Open Sans, Segoe UI, sans-serif",
      fontSize: "14px",
      fontSmoothing: "antialiased",
      ":-webkit-autofill": { color: "#000" },
      "::placeholder": { color: "#a9a9a9" },
    },
    invalid: {
      iconColor: "#000",
      color: "#000",
    },
  },
};

const validationSchema = {
  name: {
    required: true,
  },
};

@observer
class RansomPayment extends Component {
  @observable formStore = null;

  constructor(props) {
    super(props);

    document.title = "Tour - Release";

    this.formStore = new FormStore({
      name: "",
      existingCreditCardId: "",
      isDefaultCreditCard: false,
      saveCreditCard: false,
    });

    this.state = {
      isLoading: false,
      paymentSuccess: false,
      useCardOnFile: false,
      lastCardEvent: {
        error: false,
        complete: false,
      },
    };
  }

  componentDidMount() {
    const { stripeConnect } = this.props;
    const { setProperty } = this.formStore;

    if (stripeConnect.creditCards.length > 0) {
      this.setState({ useCardOnFile: true });
      setProperty("existingCreditCardId", stripeConnect.defaultCreditCard.id);
    }
  }

  async submit() {
    const { showError, stripe, elements, customerPayment, onPay, successRedirectUrl } = this.props;
    const { useCardOnFile } = this.state;
    const { model } = this.formStore;
    const { name, existingCreditCardId, isDefaultCreditCard, saveCreditCard } = model;

    const data = {
      stripeToken: null,
      saveCreditCard: false,
      existingCreditCardId: null,
      isDefaultCreditCard: false,
    };

    const onSuccess = () => {
      this.setState({ paymentSuccess: true });
      window.location.href = successRedirectUrl;
    };

    const onError = error => {
      this.setState({ isLoading: false });
      showError(error);
    };

    // check which form the user submitted.
    if (useCardOnFile) {
      data.existingCreditCardId = existingCreditCardId;
      data.isDefaultCreditCard = isDefaultCreditCard;

      this.setState({ isLoading: true });

      return onPay(customerPayment.customerPaymentId, data)
        .then(onSuccess)
        .catch(onError);
    }

    // action is to use a new card. For this, we need to make sure that stripe has loaded.
    if (!stripe || !elements) {
      throw new Error("Stripe is taking longer than expected to load. Please try again in a moment");
    }

    const cardElement = elements.getElement(CardElement);

    this.setState({ isLoading: true });

    const { error, token } = await stripe.createToken(cardElement, { name });

    if (error) {
      onError(error);
      return Promise.resolve();
    }

    data.saveCreditCard = saveCreditCard;
    data.stripeToken = token.id;

    return onPay(customerPayment.customerPaymentId, data)
      .then(onSuccess)
      .catch(onError);
  }

  render() {
    const { stripeConnect, customerPayment, onCancel, t } = this.props;
    const { lastCardEvent, useCardOnFile, isLoading, paymentSuccess } = this.state;

    const { error: cardError, complete } = lastCardEvent;
    const { model, bindInput, setProperty } = this.formStore;
    const validation = getValidationErrors(validationSchema, model);

    let releaseButtonDisabled = false;

    // when the view is "use existing card", the release button should always be enabled. Otherwise:
    if (!useCardOnFile) {
      // we are on the Use New Card view. The user should not be able to click "release" button under these
      // circumstances:
      releaseButtonDisabled = cardError || !complete || !validation.isValid;
    }

    return (
      <Modal open size="tiny" className="ransom-payment">
        <Modal.Header>{t("submitPayment")}</Modal.Header>
        <Modal.Content>
          <Modal.Description>
            {paymentSuccess && (
              <Message positive>
                <Message.Header>{t("success")}</Message.Header>
                <p>{t("reloadingPage")}</p>
              </Message>
            )}
            <Message
              icon="credit card outline"
              header={`${customerPayment.currency.toUpperCase()} $${customerPayment.amount}`}
              content={customerPayment.description}
            />
            <Form className={cx("new-card", { "use-card-on-file": useCardOnFile })} loading={isLoading}>
              <Form.Field>
                <div
                  className={cx("stripe-card-element-wrapper", {
                    error: cardError,
                    valid: !cardError && complete,
                  })}
                >
                  <CardElement options={cardOptions} onChange={e => this.setState({ lastCardEvent: e })} />
                </div>
              </Form.Field>
              <Form.Field control={Form.Input} placeholder={t("nameCard")} {...bindInput("name", validation)} />
              <Form.Field>
                <Checkbox
                  label={t("saveCardFile")}
                  checked={model.saveCreditCard}
                  onChange={() => {
                    setProperty("saveCreditCard", !model.saveCreditCard);
                  }}
                />
                {stripeConnect.creditCards.length > 0 && (
                  <a
                    href="#"
                    onClick={event => {
                      event.preventDefault();
                      this.setState({ useCardOnFile: true });
                    }}
                  >
                    {t("useCardOnFile")}
                  </a>
                )}
              </Form.Field>
            </Form>
            <Form className={cx("existing-card", { "use-card-on-file": useCardOnFile })} loading={isLoading}>
              <Form.Field>
                <Select
                  fluid
                  value={model.existingCreditCardId}
                  options={stripeConnect.creditCards.map(creditCard => {
                    return {
                      key: creditCard.id,
                      value: creditCard.id,
                      text: creditCard.label,
                    };
                  })}
                  onChange={(_e, { value }) => setProperty("existingCreditCardId", value)}
                />
              </Form.Field>
              <Form.Field>
                <Checkbox
                  label={t("saveAsDefaultCard")}
                  checked={model.isDefaultCreditCard}
                  disabled={
                    stripeConnect.defaultCreditCard && stripeConnect.defaultCreditCard.id === model.existingCreditCardId
                  }
                  onChange={() => setProperty("isDefaultCreditCard", !model.isDefaultCreditCard)}
                />
                <a
                  href="#"
                  onClick={event => {
                    event.preventDefault();
                    this.setState({ useCardOnFile: false });
                  }}
                >
                  {t("useNewCard")}
                </a>
              </Form.Field>
            </Form>
          </Modal.Description>
        </Modal.Content>
        <Modal.Actions>
          <PromiseButton
            primary
            disabled={releaseButtonDisabled || isLoading || paymentSuccess}
            onClick={() => this.submit()}
          >
            {t("payRelease")}
          </PromiseButton>
          <Button onClick={onCancel} disabled={isLoading || paymentSuccess} secondary>
            {t("cancel")}
          </Button>
        </Modal.Actions>
      </Modal>
    );
  }
}

RansomPayment.propTypes = {
  stripeConnect: PropTypes.instanceOf(StripeConnect).isRequired,
  customerPayment: PropTypes.instanceOf(CustomerPayment).isRequired,
  successRedirectUrl: PropTypes.string.isRequired,
  showError: PropTypes.func.isRequired,
  onPay: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  stripe: PropTypes.any,
  elements: PropTypes.any,
  t: PropTypes.func.isRequired,
};

export default withTranslation()(ErrorWrapper(RansomPayment));
